import { useState, useMemo, PropsWithChildren, useCallback } from "react";
import cx from "classnames";
import { Button, Flex } from "@radix-ui/themes";
import { PlusIcon } from "@radix-ui/react-icons";
import { FilterMenuType } from "@/v2/components/filter-menu";
import { ActiveFilters } from "@/v2/components/filter-menu/ActiveFilters";
import { FieldFilter } from "@/v2/components/filter-menu/FieldFilter.model";
import { FormField, FormSelectField } from "@/v2/components/form/form-schema";
import { useChat } from "../../context/ChatWrapper";
import {
  useGetInteractionAdditionalContext,
  useGetInteractionAdditionalContextFieldsOptions,
} from "@/v2/entities/interaction-additional-context/InteractionAdditionalContext.hooks";
import { useGetTopicReleasePolicyId } from "@/v2/entities/topic-release-policy/useGetTopicReleasePolicy";
import { InteractionAdditionalContextFieldsOptionsResponse } from "@/v2/entities/interaction-additional-context/InteractionAdditionalContext.api";
import { SimulatorChannel } from "@/api/simulator/simulator.model";
import { AddContextFieldsDialog } from "./AddContextFieldsDialog";
import styles from "./ChatInput.module.scss";

function getInteractionAdditionalContextFieldsOptionsSelector({
  customFields,
  enrichments,
  basicTicketData,
}: InteractionAdditionalContextFieldsOptionsResponse) {
  return [...customFields, ...enrichments, ...basicTicketData];
}

interface AdditionalContextViewProps {
  topicId: number;
  releaseId: number;
  value: Record<string, any>;
}

export function AdditionalContextView({
  topicId,
  releaseId,
  value,
}: AdditionalContextViewProps) {
  const { data: topicReleasePolicyId } = useGetTopicReleasePolicyId(
    topicId,
    releaseId,
  );

  const { data: options, isLoading: isLoadingOptions } =
    useGetInteractionAdditionalContextFieldsOptions({
      topicId,
      topicReleasePolicyId: topicReleasePolicyId ?? "",
      enabled: topicReleasePolicyId != null,
      select: getInteractionAdditionalContextFieldsOptionsSelector,
    });

  const { filterValue, filterMenu } = useMemo(() => {
    const entries: [string, FieldFilter][] = [];
    const fm: FilterMenuType<string> = [];

    const fieldMap = new Set<string>(Object.keys(value ?? {}));
    if (fieldMap.size === 0) {
      return {
        filterValue: {},
        filterMenu: [],
      };
    }

    const optionsMap = new Map<string, FormField>(
      options?.map((field) => [field.id, field]) ?? [],
    );
    Object.entries(value ?? {}).forEach(([fieldId, fieldValue]) => {
      if (fieldValue == null) return;

      const formField = optionsMap.get(fieldId);
      if (formField?.kind === "select") {
        let fieldOptions = formField.options.map(({ value, label }) => ({
          value: String(value),
          label,
        }));

        if (
          formField.allowCreate &&
          !fieldOptions.some(({ value }) => value === String(fieldValue))
        ) {
          fieldOptions.push({
            value: String(fieldValue),
            label: String(fieldValue),
          });
        }

        if (Array.isArray(fieldValue)) {
          entries.push([fieldId, { operator: "in", value: fieldValue }]);
          fm.push({
            field: fieldId,
            kind: "multi",
            label: formField.label ?? fieldId,
            options: fieldOptions,
          });
        } else {
          entries.push([fieldId, { operator: "eq", value: fieldValue }]);
          fm.push({
            field: fieldId,
            kind: "single",
            label: formField.label ?? fieldId,
            options: fieldOptions,
          });
        }

        return;
      }

      entries.push([fieldId, { operator: "eq", value: fieldValue }]);
      fm.push({
        field: fieldId,
        kind: "single",
        label: formField?.label ?? fieldId,
        options: [{ value: String(fieldValue), label: String(fieldValue) }],
      });
    });

    return {
      filterValue: Object.fromEntries(entries ?? []),
      filterMenu: fm,
    };
  }, [options, value]);

  if (isLoadingOptions) {
    return <></>;
  }

  return (
    <ActiveFilters
      size="1"
      menu={filterMenu}
      value={filterValue}
      onChange={() => {}}
      viewOnly
    />
  );
}

interface StartChatWithContextProps {
  channel: SimulatorChannel;
  topicId: number;
  releaseId: number;
}

export function StartChatWithContext({
  channel,
  topicId,
  releaseId,
  children,
}: PropsWithChildren<StartChatWithContextProps>) {
  const [open, setOpen] = useState(false);

  const { additionalContextFieldsValue, setAdditionalContextFieldsValue } =
    useChat();

  const { data: topicReleasePolicyId } = useGetTopicReleasePolicyId(
    topicId,
    releaseId,
  );

  const { data: selectedFields } = useGetInteractionAdditionalContext({
    topicId,
    topicReleasePolicyId: topicReleasePolicyId ?? "",
    enabled: topicReleasePolicyId != null,
    select: ({ fields }) => fields,
  });

  const { data: fieldOptions } =
    useGetInteractionAdditionalContextFieldsOptions({
      topicId,
      topicReleasePolicyId: topicReleasePolicyId ?? "",
      enabled: topicReleasePolicyId != null,
      select: getInteractionAdditionalContextFieldsOptionsSelector,
    });

  const fieldOptionsHelpers = useMemo(
    () => ({
      keyToLabel: new Map(
        fieldOptions?.map(({ id, label }) => [id, label]) ?? [],
      ),
      labelToKey: new Map(
        fieldOptions?.map(({ id, label }) => [label, id]) ?? [],
      ),
    }),
    [fieldOptions],
  );

  const visibleFieldOptions = useMemo<FormField[]>(() => {
    if (fieldOptions == null) {
      return [];
    }

    const fieldMap = new Set<string>(
      selectedFields?.map(({ value }) => value) ?? [],
    );
    if (fieldMap.size === 0) {
      return fieldOptions ?? [];
    }

    return fieldOptions.filter(({ id }) => fieldMap.has(id));
  }, [selectedFields, fieldOptions]);

  const show = useMemo(
    () => selectedFields != null && selectedFields.length > 0,
    [selectedFields],
  );

  const additionalContextWithoutUnknowns = useMemo(() => {
    return Object.fromEntries(
      Object.entries(additionalContextFieldsValue)
        .filter(([_, value]) => value != null && value !== "unknown")
        .map(([key, value]) => [
          fieldOptionsHelpers.labelToKey.get(key) ?? key,
          value,
        ]),
    );
  }, [additionalContextFieldsValue, fieldOptionsHelpers]);

  const buildAdditionalContextWithUnknowns = useCallback(
    (values: Record<string, any>) => {
      return Object.fromEntries(
        selectedFields?.map(({ value }) => [
          fieldOptionsHelpers.keyToLabel.get(value) ?? value,
          values[value] ?? "unknown",
        ]) ?? [],
      );
    },
    [selectedFields, fieldOptionsHelpers],
  );

  const handleSubmit = useCallback(
    (values: Record<string, any>) => {
      const additionalContext = buildAdditionalContextWithUnknowns(values);
      setAdditionalContextFieldsValue(additionalContext);
    },
    [setAdditionalContextFieldsValue, buildAdditionalContextWithUnknowns],
  );

  if (!show) {
    return (
      <div className={styles.inputContainerWrapper} data-channel={channel}>
        {children}
      </div>
    );
  }

  return (
    <>
      <div
        className={cx(styles.inputContainerWrapper, styles.showContextFields)}
        data-channel={channel}
      >
        {children}

        <Flex pt="2" px="4" gap="2" wrap="wrap">
          <Button
            highContrast
            size="1"
            color="gray"
            variant="soft"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setOpen(true);
            }}
          >
            <PlusIcon />
            Add Context
          </Button>

          <AdditionalContextView
            topicId={topicId}
            releaseId={releaseId}
            value={additionalContextWithoutUnknowns}
          />
        </Flex>
      </div>

      {open && (
        <AddContextFieldsDialog
          fields={visibleFieldOptions}
          values={additionalContextWithoutUnknowns}
          onClose={() => setOpen(false)}
          onSubmit={handleSubmit}
        />
      )}
    </>
  );
}
